import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useState } from "react";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import MembersContext from "../MembersContext";
import type { CompanyWithAccess } from "../definitions";
import CompaniesSelectDialog from "./CompaniesSelectDialog";
import CompanyWithAccessPermissions from "./CompanyWithAccessPermissions";
import InviteMembersContext from "./InviteMembersContext";

const CompaniesManageSection = () => {
  const { companiesList } = useDefinedContext(MembersContext);
  const { selectedCompanies, updateSelectedCompanies, onCompanyDelete } = useDefinedContext(InviteMembersContext);

  const [showDialog, setShowDialog] = useState(false);
  const [search, setSearch] = useState<string>("");

  const handleCompanyAdd = () => {
    setShowDialog(true);
  };

  const handleCloseAddCompaniesDialog = (companies?: CompanyWithAccess[]) => {
    setShowDialog(false);
    if (companies) {
      updateSelectedCompanies(companies);
    }
  };

  const handleSearch = useDebounce((search: string) => {
    setSearch(search);
  }, 300);

  const searchQuery = search.trim().toLowerCase();
  const filteredCompaniesList = selectedCompanies.filter((company) =>
    companiesList.some(
      (c) =>
        c.value === company.clientCode &&
        (c.label.toLowerCase().includes(searchQuery) || c.value.toLowerCase().includes(searchQuery))
    )
  );

  return (
    <Stack spacing={1} sx={{ mt: 1, alignItems: "flex-start" }}>
      <Stack direction={"row"} sx={{ alignItems: "center", gap: 1, width: "100%", mb: 1 }}>
        <Typography variant="subtitle1">Companies</Typography>
        {selectedCompanies.length > 0 && <Chip label={filteredCompaniesList.length} />}
        <Button variant="text" startIcon={<AddIcon />} onClick={handleCompanyAdd}>
          Add Company
        </Button>
        {selectedCompanies.length > 3 && (
          <Box sx={{ marginLeft: "auto" }}>
            <SearchField onSearch={handleSearch} />
          </Box>
        )}
      </Stack>
      {filteredCompaniesList.length > 0 && (
        <Stack spacing={2} sx={{ width: "100%" }}>
          {filteredCompaniesList.map((company) => (
            <CompanyWithAccessPermissions key={company.clientCode} company={company} onRemove={onCompanyDelete} />
          ))}
        </Stack>
      )}
      <CompaniesSelectDialog
        open={showDialog}
        availableCompanies={companiesList}
        onClose={handleCloseAddCompaniesDialog}
        selectedCompanies={selectedCompanies}
      />
    </Stack>
  );
};

export default CompaniesManageSection;
